import { useState } from 'react';
import { Loader } from 'lucide-react';
import moment from 'moment';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';

import IconClose from '@/assets/svg/icon-close.svg?react';
import IconSearchDatetime from '@/assets/svg/search-datatime.svg?react';
import IconDNZ from '@/assets/svg/search-dnz.svg?react';
import IconErdpou from '@/assets/svg/search-erdpou.svg?react';
import IconStatus from '@/assets/svg/search-status.svg?react';
import IconTags from '@/assets/svg/search-tags.svg?react';
import IconVin from '@/assets/svg/search-vin.svg?react';

import { Button } from '@/components/ui/button';
import { FileSearch } from '@/components/FileSearch/FileSearch';
import ParticleBackground from '@/components/particles-background';

import { SearchFileParam, useFileSearchStore } from '@/stores/fileSearchStore';
import { useFiltersStore } from '@/stores/filtersStore';
import { useSearchStore } from '@/stores/searchStore';

import { formatDateRangeForDisplay } from '@/utils/date-utils';

import { executorRoutes } from '@/constants/route-settings';
import {
  SEARCH_PARAMS_FILES,
  SEARCH_PARAMS_FILES_LIST,
} from '@/constants/search-params';

export const FileSearchPage = () => {
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const { searchHistory, clearSearchHistoryById, setSearchHistory } =
    useFileSearchStore();
  const navigate = useNavigate();
  const { searchAgreementsUuids, setSearchPageResults } = useSearchStore();
  const { setFilterByIds } = useFiltersStore();

  const handleSearch = async (params: {
    field: string;
    value: string;
    searchId: string;
  }) => {
    if (!params.field || !params.value) return;
    try {
      const searchParam = SEARCH_PARAMS_FILES_LIST.find(
        (param) => param.id === params.searchId,
      );

      if (searchParam) {
        const newHistoryItem = {
          id: uuidv4(),
          field: params.field,
          searchId: params.searchId,
          value: params.value,
          date: new Date().toISOString(),
          type: searchParam.label,
        };

        setSearchHistory([newHistoryItem, ...searchHistory]);
        setIsLoading(newHistoryItem.id);
        const agreementIds = await searchAgreementsUuids({
          field: params.field,
          value: params.value,
          id: params.searchId,
        });

        // Format date range for display if it's a date field
        const isDateField =
          params.field === SEARCH_PARAMS_FILES.UPLOAD_DATE.field ||
          params.field === SEARCH_PARAMS_FILES.FILMED_DATE.field;

        const displayValue = isDateField
          ? formatDateRangeForDisplay(params.value)
          : params.value;

        navigateToResultPage(agreementIds, displayValue, params.value);
        setIsLoading(null);
      }
    } catch (error) {}
  };

  const navigateToResultPage = (
    agreementIds: string[],
    displaySearchTerm: string,
    originalSearchTerm: string,
  ) => {
    setSearchPageResults([]);
    setFilterByIds({}, 'search');

    navigate(executorRoutes.fileSearchResult, {
      state: {
        agreementIds,
        searchTerm: displaySearchTerm,
        originalSearchTerm,
      },
    });
  };

  const getAgreementIdsBySearchTerm = async ({
    field,
    value,
    searchId,
  }: {
    field: string;
    value: string;
    id: string;
    searchId: string;
  }) => {
    return await searchAgreementsUuids({
      field,
      value,
      id: searchId,
    });
  };

  const renderIconBySearchParam = (searchId: string) => {
    switch (searchId) {
      case SEARCH_PARAMS_FILES.VIN_NUMBER.id:
        return <IconVin className="fill-red" />;

      case SEARCH_PARAMS_FILES.FILMED_DATE.id:
      case SEARCH_PARAMS_FILES.UPLOAD_DATE.id:
        return <IconSearchDatetime className="fill-red" />;

      case SEARCH_PARAMS_FILES.STATE_LICENSE_PLATE.id:
        return <IconDNZ className="fill-red" />;

      case SEARCH_PARAMS_FILES.STATUSES.id:
        return <IconStatus className="fill-red" />;

      case SEARCH_PARAMS_FILES.TAGS.id:
        return <IconTags className="fill-red" />;

      case SEARCH_PARAMS_FILES.EDRPOU_RNOKPP.id:
        return <IconErdpou className="fill-red" />;

      default:
        return <IconStatus className="fill-red" />;
    }
  };

  const handleClickHistoryItem = async (item: SearchFileParam) => {
    setIsLoading(item.id);
    const agreementIds = await getAgreementIdsBySearchTerm(item);

    setIsLoading(null);

    // Format date range for display if it's a date field
    const isDateField =
      item.field === SEARCH_PARAMS_FILES.UPLOAD_DATE.field ||
      item.field === SEARCH_PARAMS_FILES.FILMED_DATE.field;

    const displayValue = isDateField
      ? formatDateRangeForDisplay(item.value)
      : item.value;

    navigateToResultPage(agreementIds, displayValue, item.value);
  };

  // Format the display value for history items
  const getDisplayValue = (item: SearchFileParam) => {
    const isDateField =
      item.field === SEARCH_PARAMS_FILES.UPLOAD_DATE.field ||
      item.field === SEARCH_PARAMS_FILES.FILMED_DATE.field;

    return isDateField ? formatDateRangeForDisplay(item.value) : item.value;
  };

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-page-background">
      <div className="absolute left-0 top-0 z-10 h-48 w-full overflow-hidden">
        <ParticleBackground />
      </div>
      <div className="relative z-20 mx-auto flex w-full max-w-4xl flex-grow flex-col overflow-hidden px-4 pb-6 pt-12 sm:px-6 sm:pb-12 sm:pt-24">
        <h1 className="mb-6 flex-shrink-0 text-2xl font-semibold text-white">
          Ваш пошуковий запит
        </h1>
        <div className="mb-8 flex-shrink-0">
          <FileSearch onSearch={handleSearch} />
        </div>

        <div className="flex max-h-[700px] flex-grow flex-col overflow-y-auto rounded-md border bg-white shadow-[0px_0px_10px_2px_#00000040]">
          <div className="sticky top-0 z-10 flex flex-shrink-0 items-center justify-between border-b bg-white p-5 pr-3.5 shadow-[0px_0px_10px_2px_#00000040]">
            <h2 className="text-2xl font-semibold xl:text-xl">
              Історія пошуку
            </h2>
          </div>
          <div className="flex-grow overflow-y-auto">
            {searchHistory.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between border-b p-4 last:border-b-0"
              >
                {isLoading === item.id ? (
                  <div className="flex items-center justify-center gap-4">
                    <Loader className="animate-spin-slow" />
                  </div>
                ) : (
                  <div
                    className="flex cursor-pointer items-center gap-4"
                    onClick={() => handleClickHistoryItem(item)}
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded">
                      {renderIconBySearchParam(item.searchId)}
                    </div>
                    <div className="flex flex-col">
                      <div className="text-xs leading-4 text-gray-200">
                        {item.type}
                      </div>
                      <div className="mb-0.5 text-lg font-medium leading-4">
                        {getDisplayValue(item)}
                      </div>
                      <div className="text-xs leading-4 text-gray-200">
                        {moment(item.date).format('DD.MM.YYYY')}
                      </div>
                    </div>
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => clearSearchHistoryById(item.id)}
                >
                  <IconClose className="h-[14px] w-[14px] fill-primary stroke-primary" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
